<template>
  <div class="settings--content">
    <loading-state v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form v-else @submit.prevent="updateActiveAgentBot">
      <settings-section
        :title="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
      >
        <div class="medium-9 columns settings-item">
          <label>
            <select v-model="selectedAgentBotId">
              <option value="" disabled selected>
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
              </option>
              <option
                v-for="agentBot in agentBots"
                :key="agentBot.id"
                :value="agentBot.id"
              >
                {{ agentBot.name }}
              </option>
            </select>
          </label>
          <div class="button-container">
            <woot-submit-button
              :button-text="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
              :loading="uiFlags.isSettingAgentBot"
            />
            <woot-button
              type="button"
              :disabled="!selectedAgentBotId"
              :loading="uiFlags.isDisconnecting"
              variant="smooth"
              color-scheme="alert"
              class="button--disconnect"
              @click="disconnectBot"
            >
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
            </woot-button>
          </div>
        </div>
      </settings-section>

      <settings-section
        v-if="activeAgentBotOptions"
        title="Tirar o cliente do fluxo em caso de espera"
        sub-title="O que o converx deve fazer caso o cliente fique muito tempo parado no chatbot, sem dar resposta."
      >
        <div class="medium-9 columns settings-item">
          <label>
            Qual ação deve ser realizada?
            <select v-model="activeAgentBotOptions.action_type">
              <option :value="null" selected>Nao realizar nenhuma acao</option>
              <option value="reopen">Abrir conversa</option>
              <option value="resolve">Resolver conversa</option>
            </select>
          </label>
          <label v-if="activeAgentBotOptions.action_type != null">
            Em quanto tempo essa ação deve ser realizada?
            <select v-model="activeAgentBotOptions.execution_interval">
              <option :value="null" selected>Selecione tempo limite</option>
            <option value="2">2 minutos</option>
            <option value="5">5 minutos</option>
            <option value="10">10 minutos</option>
            <option value="15">15 minutos</option>
            <option value="20">20 minutos</option>
            <option value="30">30 minutos</option>
            <option value="60">1 hora</option>
            <option value="120">2 horas</option>
            <option value="240">4 horas</option>
            <option value="360">6 horas</option>
            <option value="480">8 horas</option>
            <option value="720">12 horas</option>
              <option value="1440">24 horas</option>
            </select>
          </label>
          <label
            v-if="
              activeAgentBotOptions.action_type === 'reopen' &&
              accountTeams.length
            "
          >
            Para qual time a conversa deve ser direcionada?
            <select v-model="activeAgentBotOptions.team_assign">
              <option :value="null" selected>Selecione um time</option>
            <option
              v-for="team in accountTeams"
              :key="team.id"
              :value="team.id"
            >
                {{ team.name }}
              </option>
            </select>
          </label>
          <label
            v-if="activeAgentBotOptions.action_type != null"
          >
            Deseja adicionar um evento de log na conversa
            <select v-model="activeAgentBotOptions.event_id">
              <option :value="null" selected>Nenhum evento</option>
              <option
                v-for="event in accountEvents"
                :key="event.id"
                :value="event.id"
              >
                {{ event.event_name }}
              </option>
            </select>
          </label>
          <label
            v-if="activeAgentBotOptions.action_type != null"
          >
            Deseja adicionar um marcador na conversa
            <select v-model="activeAgentBotOptions.label_id">
              <option :value="null" selected>Nenhum marcador</option>
              <option
                v-for="label in accountLabels"
                :key="label.id"
                :value="label.id"
              >
                {{ label.title }}
              </option>
            </select>
          </label>
          <label
            v-if="activeAgentBotOptions.action_type != null"
            class="editor-wrap"
          >
            Mensagem que será encaminhada para o cliente
            <woot-message-editor
              v-model.trim="activeAgentBotOptions.resolve_message"
              class="message-editor"
              :is-format-mode="true"
              :class="{ editor_warning: error }"
              placeholder="Por motivos de falta de contato, estou encerrando esse atendimento"
              @input="activeAgentBotOptions.resolve_message = $event"
            />
            <span v-if="error" class="editor-warning__message">
              A mensagem é obrigatória
            </span>
          </label>
          <div class="button-container">
            <woot-submit-button
              button-text="Atualizar"
              :loading="uiFlags.isSettingAgentBot"
            />
          </div>
        </div>
      </settings-section>

      <settings-section
        v-if="activeAgentBotOptions"
        title="Configurações de reengajamento"
        sub-title="Envie mensagens ao cliente tentando resgatá-lo para o fluxo do bot antes que a conversa seja aberta ou resolvida"
      >
        <div class="medium-9 columns settings-item">
          <reengagement-action-row
            v-for="(action, index) in reengagementActions"
            :key="index"
            :action="action"
            :index="index"
            :inbox-id="inbox.id"
            @update="updateReengagementAction"
            @remove="removeReengagementAction"
          />
          <div class="button-container">
            <woot-submit-button
              button-text="Atualizar"
              :loading="uiFlags.isSettingAgentBot"
            />

            <woot-button
              v-if="canAddReengagementAction"
              type="button"
              variant="smooth"
              color-scheme="success"
              icon="add"
              class="button-reengagement-action"
              @click="addReengagementAction"
            >
              Adicionar ação
            </woot-button>
          </div>
        </div>
      </settings-section>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SettingsSection from 'dashboard/components/SettingsSection';
import LoadingState from 'dashboard/components/widgets/LoadingState';
import alertMixin from 'shared/mixins/alertMixin';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import ReengagementActionRow from './ReengagementActionRow.vue';

export default {
  components: {
    LoadingState,
    SettingsSection,
    WootMessageEditor,
    ReengagementActionRow,
  },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      error: false,
    };
  },
  computed: {
    ...mapGetters({
      agentBotOptions: 'agentBots/getActiveAgentBotOptions',
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
      teamsList: 'teams/getTeams',
      accountEvents: 'events/getEvents',
      accountLabels: 'labels/getLabels',
    }),
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
    },
    accountTeams() {
      return this.$store.getters['teams/getTeams'];
    },
    activeAgentBotOptions() {
      return this.$store.getters['agentBots/getActiveAgentBotOptions'](
        this.inbox.id
      );
    },
    hasReengagementActions() {
      return !!this.activeAgentBotOptions?.reengagement_actions?.length;
    },
    reengagementActions() {
      return this.activeAgentBotOptions?.reengagement_actions || [];
    },
    canAddReengagementAction() {
      const list = this.reengagementActions;
      return this.isReengagementActionValid(list[list.length - 1]);
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
    activeAgentBotOptions: {
      immediate: true,
      handler(options) {
        if (options && !options.reengagement_actions?.length) {
          this.$set(options, 'reengagement_actions', [
            this.emptyReengagementAction(),
          ]);
        }
      },
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
    this.$store.dispatch('agentBots/fetchAgentBotInboxOption', this.inbox.id);
    this.$store.dispatch('events/get');
    this.$store.dispatch('labels/get');
  },

  methods: {
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
          options: {
            action_type: this.activeAgentBotOptions?.action_type,
            execution_interval: this.activeAgentBotOptions?.execution_interval,
            team_assign: this.activeAgentBotOptions?.team_assign,
            resolve_message: this.activeAgentBotOptions?.resolve_message ?? '',
            event_id: this.activeAgentBotOptions?.event_id,
            label_id: this.activeAgentBotOptions?.label_id,
            reengagement_actions: this.reengagementActions.filter(action =>
              this.isReengagementActionValid(action)
            ),
          },
        });
        this.showAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    emptyReengagementAction() {
      return {
        interval: null,
        type: 'text',
        message: '',
        template_name: '',
      };
    },
    isReengagementActionValid(action) {
      if (!action || !action.interval || !action.message) return false;
      if (action.type === 'template') return !!action.template_name;
      return true;
    },
    addReengagementAction() {
      if (!this.activeAgentBotOptions.reengagement_actions) {
        this.$set(this.activeAgentBotOptions, 'reengagement_actions', []);
      }
      this.activeAgentBotOptions.reengagement_actions.push(
        this.emptyReengagementAction()
      );
    },
    updateReengagementAction(index, action) {
      if (!this.activeAgentBotOptions.reengagement_actions) {
        this.$set(this.activeAgentBotOptions, 'reengagement_actions', []);
      }
      this.activeAgentBotOptions.reengagement_actions.splice(index, 1, action);
    },
    removeReengagementAction(index) {
      if (!this.activeAgentBotOptions.reengagement_actions) {
        return;
      }
      this.activeAgentBotOptions.reengagement_actions.splice(index, 1);
      if (!this.activeAgentBotOptions.reengagement_actions.length) {
        this.activeAgentBotOptions.reengagement_actions.push(
          this.emptyReengagementAction()
        );
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        this.showAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        this.showAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
.button--disconnect,.button-reengagement-action {
  margin-left: var(--space-small);
}
</style>
